import { Input } from "@/components/ui/input"
import { H4, Small } from "@/components/ui/typography"
import { cn } from "@/lib/utils"
import { Edit, Import, MapIcon, MoreHorizontal, Trash2 } from "lucide-react"
import { MagicWandIcon } from "@radix-ui/react-icons"
import React from "react"
import {
	useReactTable,
	getCoreRowModel,
	flexRender,
	createColumnHelper,
	getPaginationRowModel,
	getFilteredRowModel,
	Row,
} from "@tanstack/react-table"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table"
import { Checkbox } from "@/components/ui/checkbox"
import { Button } from "@/components/ui/button"
import { TablePagination } from "@/components/primitives/TablePagination"
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { SettingsLayout } from "@/components/layouts/setting-layout/SettingsLayout"
import { addressSchema, AddressType } from "@/state/draft/hqAddress"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import { AddAddress } from "@/components/forms/AddAddress"
import { DialogTrigger } from "@radix-ui/react-dialog"
import { getLatLng } from "use-places-autocomplete"
import { useFieldArray, useForm } from "react-hook-form"
import { useFormAutoSave } from "@/hooks/useFormAutoSave"
import { zodResolver } from "@hookform/resolvers/zod"
import { FormField, FormMessage } from "@/components/ui/form"
import { AddressAutocompleteContainer } from "../../primitives/AddressAutocomplete"
import { z } from "zod"
import { RHFProvider } from "@/components/forms/RHFProvider"
import { useAnalytics } from "@/hooks/use-analytics"
import { RestoreAddress, RestoreAddressForm } from "@/components/forms/RestoreAddress"
import { restoreAddressesWithAi } from "@/solver/onboard-algo"
import { useToast } from "@/hooks/use-toast"

type EditAddressScreenProps = {
	reportId: string
	hqAddress: AddressType
	clientAddresses: AddressType[]
	onSave: (data: FormData) => void
	onSaveLocal: (data: FormData) => void
	savingInProcess: boolean
	restoreAddresses: (data: RestoreAddressForm) => Promise<AddressType[]>
}

const columnHelper = createColumnHelper<AddressType>()
const columns = [
	columnHelper.display({
		id: "select",
		header: ({ table }) => (
			<Checkbox
				checked={
					table.getIsAllPageRowsSelected() ||
					(table.getIsSomePageRowsSelected() && "indeterminate")
				}
				onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
				aria-label="Select all"
			/>
		),
		cell: ({ row }) => (
			<Checkbox
				checked={row.getIsSelected()}
				onCheckedChange={(value) => row.toggleSelected(!!value)}
				aria-label="Select row"
			/>
		),
		size: 20,
	}),
	columnHelper.accessor("address", {
		header: "Address",
		cell: (info) => info.getValue(),
		size: 500,
	}),
	columnHelper.display({
		id: "actions",
		cell: (info) => {
			const [editOpen, setEditOpen] = React.useState(false)
			return (
				<>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="ghost" className="h-8 w-8 p-0">
								<span className="sr-only">Open menu</span>
								<MoreHorizontal className="h-4 w-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem onClick={() => setEditOpen(true)}>
								<Edit className="mr-2 h-4 w-4" />
								Edit
							</DropdownMenuItem>

							<DropdownMenuItem
								className="text-destructive"
								onClick={() =>
									//@ts-ignore

									info.table.options.meta?.onDelete(info.row)
								}
							>
								<Trash2 className="mr-2 h-4 w-4" />
								Delete
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
					<Dialog open={editOpen} onOpenChange={setEditOpen}>
						<DialogContent>
							<AddAddress
								onSaveAddress={(address) => {
									//@ts-ignore
									info.table.options.meta.onEdit(address, info.row)
									setEditOpen(false)
								}}
								value={info.row.original}
								onAddressChange={() => {}}
							/>
						</DialogContent>
					</Dialog>
				</>
			)
		},
		size: 20,
	}),
]

type FormData = { hq: AddressType; clients: AddressType[] }
export const EditAddressScreen: React.FC<EditAddressScreenProps> = (props) => {
	const { sendEvent } = useAnalytics("Edit_Address")
	const [rowSelection, setRowSelection] = React.useState({})

	const table = useReactTable({
		data: props.clientAddresses,
		columns,
		getCoreRowModel: getCoreRowModel(),
		initialState: {
			pagination: {
				pageSize: 5,
				pageIndex: 0,
			},
			rowSelection,
		},
		state: {
			rowSelection,
		},
		enableRowSelection: true,
		getPaginationRowModel: getPaginationRowModel(),
		onRowSelectionChange: setRowSelection,
		getFilteredRowModel: getFilteredRowModel(),
		meta: {
			onDelete: (row: Row<AddressType>) => {
				clients.remove(+row.id)
			},
			onEdit: (newAddr: AddressType, row: Row<AddressType>) => {
				clients.update(+row.id, newAddr)
			},
		},
	})

	const form = useForm<FormData>({
		defaultValues: { clients: props.clientAddresses, hq: props.hqAddress },
		resolver: zodResolver(
			z.object({ hq: addressSchema, clients: z.array(addressSchema) })
		),
	})

	useFormAutoSave(form.control, props.onSaveLocal, {
		clients: props.clientAddresses,
		hq: props.hqAddress,
	})
	const clients = useFieldArray({
		control: form.control,
		name: "clients",
	})

	const [addDialogOpen, setAddDialogOpen] = React.useState(false)
	const [restoreDialogOpen, setRestoreDialogOpen] = React.useState(false)
	const [addrRestoring, setAddrRestoring] = React.useState(false)

	const handleRestoreAddress = async (data: RestoreAddressForm) => {
		setAddrRestoring(true)
		form.setValue("hq", data.address)
		const addresses = await props.restoreAddresses(data)
		clients.append(addresses)
		setRestoreDialogOpen(false)
		setAddrRestoring(false)
	}
	return (
		<SettingsLayout
			savingInProcess={props.savingInProcess}
			reportId={props.reportId}
			title="Address"
			activeSection="addresses"
			description="Use AI to add or restore addresses for your report."
			onSave={() => {
				form.handleSubmit((data) => {
					// TODO: fixed when form validation is fixed
					props.onSave(data)
				})()
			}}
		>
			<RHFProvider form={form}>
				<div className="flex flex-col gap-1.5">
					<H4>1. Select the start address</H4>
					<div>
						<Small className="text-muted-foreground font-normal">
							Select the address where you typically begin your business
							trips.
						</Small>
						<Small className="text-muted-foreground font-normal">
							Usually, this is your office or home address.
						</Small>
					</div>
					<div className="max-w-[350px]">
						<FormField
							name="hq.address"
							control={form.control}
							render={({ field, fieldState }) => {
								return (
									<>
										<AddressAutocompleteContainer
											onChangeAddress={(e) => {
												const { lat, lng } = getLatLng(e)
												form.setValue(
													"hq.address",
													e.formatted_address
												)
												form.setValue("hq.latlon", [lat, lng])
											}}
											inputProps={{
												...field,
												style: { width: "100%" },
												placeholder: "Enter your start address",
											}}
											input={
												<Input
													type="text"
													className=""
													endIcon={
														<MapIcon
															size={16}
															className="text-muted-foreground"
														/>
													}
												/>
											}
										/>
										{!!fieldState.error && (
											<FormMessage>
												Please enter a valid address
											</FormMessage>
										)}
									</>
								)
							}}
						/>
					</div>
				</div>
				<div className="flex flex-col gap-3.5 mt-8">
					<H4>2. Enter business-related addresses you've visited</H4>
					<div className="flex gap-4 w-full">
						<Dialog
							open={restoreDialogOpen}
							onOpenChange={setRestoreDialogOpen}
						>
							<DialogTrigger>
								<ActionCard
									onClick={() => {
										sendEvent("RestoreAddresses__View")
									}}
									icon={<MagicWandIcon className="size-6" />}
									title="Restore with AI"
								/>
							</DialogTrigger>
							<DialogContent
								onInteractOutside={(e) => {
									if (addrRestoring) e.preventDefault()
								}}
							>
								<RestoreAddress
									defaultValues={{
										address: form.getValues("hq"),
										count: 10,
										radiusMiles: 3,
									}}
									restoringInProgress={addrRestoring}
									onRestore={(data) => {
										sendEvent("RestoreAddresses__Generation")
										handleRestoreAddress(data)
									}}
								/>
							</DialogContent>
						</Dialog>

						<Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
							<DialogTrigger>
								<ActionCard
									onClick={() => {
										sendEvent("ManualAddAddress__View")
									}}
									icon={<Edit className="size-6" />}
									title="Add Manually"
								/>
							</DialogTrigger>
							<DialogContent>
								<AddAddress
									onAddressChange={() => {}}
									onSaveAddress={(address) => {
										sendEvent("ManualAddAddress__Save")
										clients.append(address)
										setAddDialogOpen(false)
									}}
								/>
							</DialogContent>
						</Dialog>

						<ActionCard
							icon={<Import className="size-6" />}
							title="Import Addresses"
							className="opacity-50 cursor-not-allowed"
						/>
					</div>

					<Table className="rounded-lg">
						<TableHeader>
							{table.getHeaderGroups().map((headerGroup) => (
								<TableRow key={headerGroup.id}>
									{headerGroup.headers.map((header) => (
										<TableHead
											key={header.id}
											style={{ width: header.getSize() }}
											className="px-4"
										>
											{header.isPlaceholder
												? null
												: flexRender(
														header.column.columnDef.header,
														header.getContext()
												  )}
										</TableHead>
									))}
								</TableRow>
							))}
						</TableHeader>
						<TableBody>
							{table.getRowModel().rows.map((row) => (
								<TableRow
									key={row.id}
									data-state={row.getIsSelected() && "selected"}
									className="data-[state=selected]:bg-[#f6f6f7]"
								>
									{row.getVisibleCells().map((cell) => (
										<TableCell key={cell.id} className="px-4">
											{flexRender(
												cell.column.columnDef.cell,
												cell.getContext()
											)}
										</TableCell>
									))}
								</TableRow>
							))}
						</TableBody>
					</Table>
					<div className="flex justify-between items-center">
						<div className="flex justify-between items-center">
							{table.getFilteredSelectedRowModel().rows.length > 0 && (
								<Button
									variant="destructive"
									size="sm"
									onClick={() => {
										clients.remove(
											table
												.getFilteredSelectedRowModel()
												.rows.map((x) => +x.id)
										)
										setRowSelection({})
									}}
								>
									<Trash2 className="mr-2 h-4 w-4" />
									Delete{" "}
									{table.getFilteredSelectedRowModel().rows.length}{" "}
									selected
								</Button>
							)}
						</div>
						<TablePagination
							canNextPage={table.getCanNextPage()}
							canPreviousPage={table.getCanPreviousPage()}
							currentPage={table.getState().pagination.pageIndex + 1}
							onFirstPage={() => table.setPageIndex(0)}
							onLastPage={() =>
								table.setPageIndex(table.getPageCount() - 1)
							}
							onNextPage={() => table.nextPage()}
							onPreviousPage={() => table.previousPage()}
							pageCount={table.getPageCount()}
						/>
					</div>
				</div>
			</RHFProvider>
		</SettingsLayout>
	)
}

interface ActionCardProps extends React.HTMLAttributes<HTMLDivElement> {
	icon?: React.ReactNode
	title: string
	disabled?: boolean
}

const ActionCard = React.forwardRef<HTMLDivElement, ActionCardProps>(
	({ className, icon: Icon, title, ...props }, ref) => {
		return (
			<div
				ref={ref}
				className={cn(
					"border-2 rounded-lg items-center py-3 px-10 flex-grow flex flex-col gap-3",
					!props.disabled && "cursor-pointer hover:bg-accent",
					props.disabled && "opacity-50 cursor-not-allowed",
					className
				)}
				{...props}
			>
				{Icon}
				{title}
			</div>
		)
	}
)
ActionCard.displayName = "ActionCard"
